import { Briefcase, ChevronDown, Users, Video } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RecruitmentShell from "../components/RecruitmentShell";
import JobCreateRichTextField from "../components/JobCreateRichTextField";
import JobPostPreviewModal from "../components/JobPostPreviewModal";
import SalaryCurrencyIcon from "../components/SalaryCurrencyIcon";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import { formatSeniorityDisplay, SALARY_CURRENCY_OPTIONS, SENIORITY_LEVEL_VALUES } from "./applicantData";
import {
  clearHiringJobCreateAuthRecovery,
  consumeHiringJobCreatePreviewResumeAsync,
  hasPendingHiringJobCreatePreviewResume,
  loadHiringJobCreateAuthRecovery,
  loadHiringJobDraft,
  resolveHiringDraftRoleId,
  isJobPostDraftQueryId,
  saveHiringJobCreateAuthRecovery,
  saveHiringJobDraft,
} from "../data/hiringJobDraftPrefill";
import { buildJobPostPreviewSnapshot } from "../data/jobPostPreview";
import { COUNTRIES } from "../data/countriesList";
import { APPLICANT_COUNTRY_GROUPS } from "../data/applicantCountryGroups";
import WorkCountriesPillPicker from "../components/WorkCountriesPillPicker";
import PitchVideoPlayer from "../components/PitchVideoPlayer";
import PitchRecordModal from "../components/PitchRecordModal";
import { captureAnalyticsEvent } from "../lib/analytics";
import {
  deleteHiringJobPitchVideo,
  getHiringJobPost,
  patchHiringJobPost,
  postHiringJobPost,
  postHiringJobPitchVideo,
} from "../apiClient";
import {
  EMPTY_QUILL_HTML,
  getEditorTextLength,
  normalizeRichTextStorage,
  stripEditorHtml,
} from "../lib/richTextEditor";

const MAX_FUNCTIONAL_AREA_PILLS = 5;
const MAX_FUNCTIONAL_AREA_CHARS = 200;

function parseFunctionalAreaSegments(serialized) {
  return String(serialized || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function serializeFunctionalAreaSegments(segments) {
  return segments.join(", ");
}

function clampFunctionalAreaSegmentsToCharBudget(segments, maxChars) {
  const out = [];
  for (const s of segments) {
    const candidate = out.length ? `${out.join(", ")}, ${s}` : s;
    if (candidate.length <= maxChars) out.push(s);
    else break;
  }
  return out;
}

const INITIAL_FORM = {
  title: "",
  functionalArea: "",
  country: "",
  city: "",
  seniorityLevel: "",
  workMode: "",
  employmentType: "",
  salaryCurrency: "",
  salaryMin: "",
  salaryMax: "",
  summary: "",
  responsibilities: EMPTY_QUILL_HTML,
  requirements: EMPTY_QUILL_HTML,
  defaultApplyAudience: "anyone",
  defaultApplyCountryCodes: [],
};

const SALARY_CURRENCY_VALUE_SET = new Set(SALARY_CURRENCY_OPTIONS.map((o) => o.value));
const MAX_DEFAULT_APPLY_COUNTRY_CODES = 50;
const JOB_CREATE_COUNTRY_ISO_SET = new Set(COUNTRIES.map((c) => c.value));
const JOB_CREATE_EEA_GROUP = APPLICANT_COUNTRY_GROUPS.find((g) => g.id === "EEA") ?? null;

function hasJobSalaryCurrency(value) {
  const c = String(value ?? "").trim().toUpperCase();
  return SALARY_CURRENCY_VALUE_SET.has(c);
}

/**
 * Hiring recruitment default (`users.default_advertising_currency` → session `recruitmentProfile.defaultAdvertisingCurrency`).
 * Returns "" when unset or invalid so the job form can require an explicit choice.
 */
function readRecruitmentSalaryCurrencyForJobCreate(currentUser) {
  const c = String(currentUser?.recruitmentProfile?.defaultAdvertisingCurrency ?? "")
    .trim()
    .toUpperCase();
  return SALARY_CURRENCY_VALUE_SET.has(c) ? c : "";
}

function buildJobCreateInitialFormSnapshot(currentUser) {
  return {
    ...INITIAL_FORM,
    country: readRecruitmentDefaultJobCountry(currentUser),
    city: readRecruitmentDefaultJobTownCity(currentUser),
    salaryCurrency: readRecruitmentSalaryCurrencyForJobCreate(currentUser),
    defaultApplyAudience: readRecruitmentDefaultApplyAudience(currentUser),
    defaultApplyCountryCodes: readRecruitmentDefaultApplyCountryCodes(currentUser),
  };
}

function readRecruitmentDefaultJobCountry(currentUser) {
  const c = String(currentUser?.recruitmentProfile?.defaultJobCountry ?? "")
    .trim()
    .toUpperCase();
  return JOB_CREATE_COUNTRY_ISO_SET.has(c) ? c : "";
}

function readRecruitmentDefaultJobTownCity(currentUser) {
  const s = String(currentUser?.recruitmentProfile?.defaultJobTownCity ?? "").trim();
  return s;
}

/** Prefer saved job currency, then recruitment default, else unset (""). */
function coalesceJobSalaryCurrencyFromPost(jobPostCurrency, currentUser) {
  const v = String(jobPostCurrency ?? "").trim().toUpperCase();
  if (SALARY_CURRENCY_VALUE_SET.has(v)) return v;
  return readRecruitmentSalaryCurrencyForJobCreate(currentUser);
}

function readRecruitmentDefaultApplyAudience(currentUser) {
  const v = currentUser?.recruitmentProfile?.defaultApplyAudience;
  return v === "countries" ? "countries" : "anyone";
}

function readRecruitmentDefaultApplyCountryCodes(currentUser) {
  const a = currentUser?.recruitmentProfile?.defaultApplyCountryCodes;
  if (!Array.isArray(a)) return [];
  const out = [];
  const seen = new Set();
  for (const item of a) {
    const c = String(item || "").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(c) || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
    if (out.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) break;
  }
  return out;
}

function readJobPostApplyFromPost(jobPost) {
  const aud = jobPost?.defaultApplyAudience === "countries" ? "countries" : "anyone";
  const raw = jobPost?.defaultApplyCountryCodes;
  const codes = [];
  if (Array.isArray(raw)) {
    const seen = new Set();
    for (const item of raw) {
      const c = String(item || "").trim().toUpperCase();
      if (!/^[A-Z]{2}$/.test(c) || seen.has(c)) continue;
      seen.add(c);
      codes.push(c);
      if (codes.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) break;
    }
  }
  return { defaultApplyAudience: aud, defaultApplyCountryCodes: codes };
}

/** Stable snapshot for “saved” vs current (matches recruitment profile dirty pattern). */
function mergeJobCreateAuthRecovery(prev, loaded) {
  const fields = { ...(loaded.fields && typeof loaded.fields === "object" ? loaded.fields : {}) };
  if (Array.isArray(fields.defaultApplyCountryCodes)) {
    fields.defaultApplyCountryCodes = [...fields.defaultApplyCountryCodes];
  }
  return {
    ...prev,
    ...fields,
    responsibilities: normalizeRichTextStorage(
      fields.responsibilities != null ? fields.responsibilities : prev.responsibilities
    ),
    requirements: normalizeRichTextStorage(
      fields.requirements != null ? fields.requirements : prev.requirements
    ),
  };
}

function fingerprintJobCreateState(formData, savedPitchUrl, pendingPitchKey) {
  return JSON.stringify({
    title: String(formData.title || "").trim(),
    functionalArea: String(formData.functionalArea || "").trim(),
    country: String(formData.country || "").trim(),
    city: String(formData.city || "").trim(),
    seniorityLevel: String(formData.seniorityLevel || "").trim(),
    workMode: String(formData.workMode || "").trim(),
    employmentType: String(formData.employmentType || "").trim(),
    salaryCurrency: String(formData.salaryCurrency || ""),
    salaryMin: String(formData.salaryMin ?? "").trim(),
    salaryMax: String(formData.salaryMax ?? "").trim(),
    summary: String(formData.summary || "").trim(),
    responsibilities: normalizeRichTextStorage(formData.responsibilities),
    requirements: normalizeRichTextStorage(formData.requirements),
    defaultApplyAudience: formData.defaultApplyAudience === "countries" ? "countries" : "anyone",
    defaultApplyCountryCodes: [...(formData.defaultApplyCountryCodes || [])].map((c) => String(c || "").trim().toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c)).sort(),
    savedPitchUrl: String(savedPitchUrl || "").trim(),
    pendingPitchKey: String(pendingPitchKey || ""),
  });
}

function stripPitchFromFingerprint(fp) {
  try {
    const parsed = JSON.parse(String(fp || ""));
    if (!parsed || typeof parsed !== "object") return "";
    delete parsed.savedPitchUrl;
    delete parsed.pendingPitchKey;
    return JSON.stringify(parsed);
  } catch {
    return "";
  }
}


function isAuthRecoveryError(err) {
  if (!(err instanceof Error)) return false;
  if (err.code === "UNAUTHORIZED") return true;
  const m = err.message.toLowerCase();
  return (
    m.includes("not authenticated") ||
    m.includes("not signed in") ||
    m.includes("sign in again to save")
  );
}

/**
 * Rich-text + salary range rules for Save & Publish (native `required` / `maxLength` cover the rest).
 * Same messages as before; bubbles via `form.reportValidity()` like Sign in (`checkValidity` / `reportValidity`).
 */
function syncJobCreatePublishValidity(form, formData, options = {}) {
  const { functionalAreaOverride, validateFunctionalArea = false } = options;
  const reqGate = form.querySelector("#job-create-requirements-validity");
  const respGate = form.querySelector("#job-create-responsibilities-validity");
  const minEl = form.querySelector("#job-create-salary-min");
  const maxEl = form.querySelector("#job-create-salary-max");
  const countryEl = form.querySelector("#job-create-country");
  const seniorityEl = form.querySelector("#job-create-seniority-level");
  const workModeEl = form.querySelector("#job-create-work-mode");
  const employmentTypeEl = form.querySelector("#job-create-employment-type");

  if (seniorityEl instanceof HTMLSelectElement) {
    if (!String(formData.seniorityLevel || "").trim()) {
      seniorityEl.setCustomValidity("Please select a seniority level.");
    } else {
      seniorityEl.setCustomValidity("");
    }
  }
  if (workModeEl instanceof HTMLSelectElement) {
    if (!String(formData.workMode || "").trim()) {
      workModeEl.setCustomValidity("Please select a work mode.");
    } else {
      workModeEl.setCustomValidity("");
    }
  }
  if (employmentTypeEl instanceof HTMLSelectElement) {
    if (!String(formData.employmentType || "").trim()) {
      employmentTypeEl.setCustomValidity("Please select an employment type.");
    } else {
      employmentTypeEl.setCustomValidity("");
    }
  }

  if (countryEl instanceof HTMLSelectElement) {
    if (!String(formData.country || "").trim()) {
      countryEl.setCustomValidity("Please select a job primary country.");
    } else {
      countryEl.setCustomValidity("");
    }
  }
  if (reqGate instanceof HTMLInputElement) {
    if (!stripEditorHtml(formData.requirements)) {
      reqGate.setCustomValidity("Please add Must-have Skills and Experience.");
    } else if (getEditorTextLength(formData.requirements) > 1500) {
      reqGate.setCustomValidity("Must-have Skills and Experience cannot exceed 1,500 characters.");
    } else {
      reqGate.setCustomValidity("");
    }
  }

  if (respGate instanceof HTMLInputElement) {
    if (!stripEditorHtml(formData.responsibilities)) {
      respGate.setCustomValidity("Please add a Job Description.");
    } else if (getEditorTextLength(formData.responsibilities) > 3500) {
      respGate.setCustomValidity("Job Description cannot exceed 3,500 characters.");
    } else {
      respGate.setCustomValidity("");
    }
  }

  const currencyEl = form.querySelector("#job-create-salary-currency");
  if (currencyEl instanceof HTMLSelectElement) {
    if (!hasJobSalaryCurrency(formData.salaryCurrency)) {
      currencyEl.setCustomValidity("Please select a salary currency.");
    } else {
      currencyEl.setCustomValidity("");
    }
  }

  const minNum = formData.salaryMin === "" ? NaN : Number(formData.salaryMin);
  const maxNum = formData.salaryMax === "" ? NaN : Number(formData.salaryMax);
  const salaryRangeBad =
    !Number.isNaN(minNum) && !Number.isNaN(maxNum) && maxNum < minNum;
  if (minEl instanceof HTMLInputElement) {
    minEl.setCustomValidity(
      salaryRangeBad ? "Salary minimum cannot be greater than salary maximum." : ""
    );
  }
  if (maxEl instanceof HTMLInputElement) {
    maxEl.setCustomValidity(
      salaryRangeBad ? "Salary maximum cannot be less than salary minimum." : ""
    );
  }

  const applyGate = form.querySelector("#job-create-default-apply-validity");
  if (applyGate instanceof HTMLInputElement) {
    const aud = formData.defaultApplyAudience === "countries" ? "countries" : "anyone";
    if (aud === "countries" && (!Array.isArray(formData.defaultApplyCountryCodes) || formData.defaultApplyCountryCodes.length === 0)) {
      applyGate.setCustomValidity("Select at least one country, or choose “Anyone on JOBUWIN”.");
    } else {
      applyGate.setCustomValidity("");
    }
  }

  const functionalGate = form.querySelector("#job-create-functional-area-validity");
  if (functionalGate instanceof HTMLInputElement) {
    if (!validateFunctionalArea) {
      functionalGate.setCustomValidity("");
    } else {
      const faRaw = functionalAreaOverride != null ? functionalAreaOverride : formData.functionalArea;
      const fa = String(faRaw || "").trim();
      const segments = parseFunctionalAreaSegments(fa);
      if (segments.length === 0) {
        functionalGate.setCustomValidity("Please add at least one functional area.");
      } else if (segments.length > MAX_FUNCTIONAL_AREA_PILLS) {
        functionalGate.setCustomValidity(`You can add up to ${MAX_FUNCTIONAL_AREA_PILLS} functional areas.`);
      } else if (fa.length > MAX_FUNCTIONAL_AREA_CHARS) {
        functionalGate.setCustomValidity(
          `Functional areas cannot exceed ${MAX_FUNCTIONAL_AREA_CHARS} characters in total.`
        );
      } else {
        functionalGate.setCustomValidity("");
      }
    }
  }
}

/** Clears `setCustomValidity` on gates used by `syncJobCreatePublishValidity` (e.g. after draft “nothing to save” handling). */
function clearJobCreatePublishCustomValidity(form) {
  if (!(form instanceof HTMLFormElement)) return;
  const ids = [
    "#job-create-requirements-validity",
    "#job-create-responsibilities-validity",
    "#job-create-salary-min",
    "#job-create-salary-max",
    "#job-create-country",
    "#job-create-seniority-level",
    "#job-create-work-mode",
    "#job-create-employment-type",
    "#job-create-salary-currency",
    "#job-create-default-apply-validity",
    "#job-create-functional-area-validity",
  ];
  for (const sel of ids) {
    const el = form.querySelector(sel);
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
      el.setCustomValidity("");
    }
  }
}

function jobApplyRulesHaveDraftSignal(formData) {
  return (
    formData.defaultApplyAudience === "countries" ||
    (Array.isArray(formData.defaultApplyCountryCodes) && formData.defaultApplyCountryCodes.length > 0)
  );
}

function hasAnyDraftContent(formData, savedPitchUrl, pitchPendingBlobRef) {
  const pendingPitch =
    pitchPendingBlobRef?.current instanceof Blob && pitchPendingBlobRef.current.size > 0;
  return Boolean(
    formData.title.trim() ||
      formData.functionalArea.trim() ||
      String(formData.country || "").trim() ||
      formData.city.trim() ||
      String(formData.seniorityLevel || "").trim() ||
      String(formData.workMode || "").trim() ||
      String(formData.employmentType || "").trim() ||
      formData.salaryMin.trim() ||
      formData.salaryMax.trim() ||
      formData.summary.trim() ||
      stripEditorHtml(formData.requirements) ||
      stripEditorHtml(formData.responsibilities) ||
      savedPitchUrl ||
      pendingPitch ||
      jobApplyRulesHaveDraftSignal(formData)
  );
}

const FunctionalAreaPillsInput = forwardRef(function FunctionalAreaPillsInput(
  { composeId, value, onChange, placeholder },
  ref
) {
  const pills = useMemo(() => parseFunctionalAreaSegments(value), [value]);
  const [draft, setDraft] = useState("");
  const draftRef = useRef("");
  const valueRef = useRef(value);
  valueRef.current = value;
  draftRef.current = draft;

  useImperativeHandle(
    ref,
    () => ({
      flushDraftAndGetSerialized() {
        const frag = String(draftRef.current || "").trim();
        let next = [...parseFunctionalAreaSegments(valueRef.current)];
        if (frag && next.length < MAX_FUNCTIONAL_AREA_PILLS) {
          next = [...next, frag].slice(0, MAX_FUNCTIONAL_AREA_PILLS);
        }
        setDraft("");
        const clamped = clampFunctionalAreaSegmentsToCharBudget(next, MAX_FUNCTIONAL_AREA_CHARS);
        const serialized = serializeFunctionalAreaSegments(clamped);
        const prev = String(valueRef.current || "").trim();
        if (serialized !== prev) {
          onChange({ target: { value: serialized } });
        }
        return serialized;
      },
      clearComposeDraft() {
        setDraft("");
      },
    }),
    [onChange]
  );

  const commitDraft = () => {
    const frag = draft.trim();
    if (!frag) return;
    if (pills.length >= MAX_FUNCTIONAL_AREA_PILLS) {
      setDraft("");
      return;
    }
    let next = [...pills, frag].slice(0, MAX_FUNCTIONAL_AREA_PILLS);
    setDraft("");
    const clamped = clampFunctionalAreaSegmentsToCharBudget(next, MAX_FUNCTIONAL_AREA_CHARS);
    onChange({ target: { value: serializeFunctionalAreaSegments(clamped) } });
  };

  const removePill = (index) => {
    const next = pills.filter((_, i) => i !== index);
    onChange({ target: { value: serializeFunctionalAreaSegments(next) } });
  };

  const onComposeChange = (e) => {
    const raw = e.target.value;
    if (!raw.includes(",")) {
      setDraft(raw);
      return;
    }
    const parts = raw.split(",");
    let working = [...pills];
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i].trim();
      if (!seg) continue;
      if (working.length >= MAX_FUNCTIONAL_AREA_PILLS) break;
      working.push(seg);
    }
    working = clampFunctionalAreaSegmentsToCharBudget(working, MAX_FUNCTIONAL_AREA_CHARS);
    const tail = parts[parts.length - 1] ?? "";
    setDraft(tail);
    const prevSerialized = serializeFunctionalAreaSegments(pills);
    const nextSerialized = serializeFunctionalAreaSegments(working);
    if (nextSerialized !== prevSerialized) {
      onChange({ target: { value: nextSerialized } });
    }
  };

  const onComposeKeyDown = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === "Backspace" && draft === "") {
      if (!pills.length) return;
      e.preventDefault();
      const next = pills.slice(0, -1);
      onChange({ target: { value: serializeFunctionalAreaSegments(next) } });
    }
  };

  const onComposeBlur = () => {
    commitDraft();
  };

  return (
    <div className="job-create-functional-area-shell">
      {pills.map((pill, index) => (
        <span key={`${index}:${pill}`} className="job-create-functional-area-pill">
          {pill}
          <button
            type="button"
            className="job-create-functional-area-pill-remove"
            aria-label={`Remove ${pill}`}
            onClick={() => removePill(index)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={composeId}
        type="text"
        className="job-create-functional-area-compose"
        value={draft}
        onChange={onComposeChange}
        onKeyDown={onComposeKeyDown}
        onBlur={onComposeBlur}
        placeholder={pills.length === 0 && !draft ? placeholder : ""}
        aria-label="Add functional area"
      />
    </div>
  );
});

FunctionalAreaPillsInput.displayName = "FunctionalAreaPillsInput";

function getInitialJobCreateFormFromWindowUrl(currentUser) {
  const base = buildJobCreateInitialFormSnapshot(currentUser);
  if (typeof window === "undefined") return base;
  const id = new URLSearchParams(window.location.search).get("draft");
  if (!id) return base;
  const merged = loadHiringJobDraft(id, base);
  return {
    ...merged,
    responsibilities: normalizeRichTextStorage(merged.responsibilities),
    requirements: normalizeRichTextStorage(merged.requirements),
  };
}

function cloneJobCreateFormData(fd) {
  return {
    ...fd,
    responsibilities: normalizeRichTextStorage(fd.responsibilities),
    requirements: normalizeRichTextStorage(fd.requirements),
    defaultApplyCountryCodes: [...(Array.isArray(fd.defaultApplyCountryCodes) ? fd.defaultApplyCountryCodes : [])],
  };
}

function CreateJobOpeningPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const profilePhotoSrc = currentUser?.photoDataUrl || profilePlaceholder;
  const draftRoleNavRef = useRef(null);
  const suppressServerDraftFormHydrateOnceRef = useRef(false);
  const [hydrationAllowed, setHydrationAllowed] = useState(
    () => typeof window === "undefined" || !hasPendingHiringJobCreatePreviewResume()
  );
  const [formData, setFormData] = useState(() => getInitialJobCreateFormFromWindowUrl(currentUser));
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [jobPreviewOpen, setJobPreviewOpen] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showMobilePitchInfo, setShowMobilePitchInfo] = useState(false);
  const [applicationSettingsExpanded, setApplicationSettingsExpanded] = useState(false);
  const [savedPitchUrl, setSavedPitchUrl] = useState("");
  const savedPitchUrlRef = useRef("");
  savedPitchUrlRef.current = savedPitchUrl;
  const [currentJobPostId, setCurrentJobPostId] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [pitchUploadBusy, setPitchUploadBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const functionalAreaRef = useRef(null);
  /** Last recorded pitch bytes until uploaded to the API (blob: preview in `savedPitchUrl` before job id exists). */
  const pitchPendingBlobRef = useRef(null);
  const pitchPendingMimeRef = useRef("");
  const pitchPendingDurationSecRef = useRef(0);
  const [pitchPendingDurationSec, setPitchPendingDurationSec] = useState(0);
  const [pitchDirtyTick, setPitchDirtyTick] = useState(0);
  const [jobApplyCountriesInputDraft, setJobApplyCountriesInputDraft] = useState("");
  const [baselineFingerprint, setBaselineFingerprint] = useState(() =>
    fingerprintJobCreateState(getInitialJobCreateFormFromWindowUrl(currentUser), "", "")
  );
  /** Mirrors each `baselineFingerprint` update so Reset can restore form + pitch without guessing from JSON. */
  const baselineJobCreateSnapshotRef = useRef(null);

  const bumpPitchDirtySignal = useCallback(() => {
    setPitchDirtyTick((n) => n + 1);
  }, []);

  const pendingPitchKey = useMemo(() => {
    const b = pitchPendingBlobRef.current;
    if (!(b instanceof Blob) || b.size === 0) return "";
    return `${b.size}:${pitchPendingMimeRef.current || ""}`;
  }, [pitchDirtyTick, savedPitchUrl]);

  const currentJobCreateFingerprint = useMemo(
    () => fingerprintJobCreateState(formData, savedPitchUrl, pendingPitchKey),
    [formData, savedPitchUrl, pendingPitchKey]
  );

  const isJobCreateDirty = currentJobCreateFingerprint !== baselineFingerprint;
  const isJobCreateDirtyIgnoringPitch =
    stripPitchFromFingerprint(currentJobCreateFingerprint) !== stripPitchFromFingerprint(baselineFingerprint);

  useLayoutEffect(() => {
    baselineJobCreateSnapshotRef.current = {
      formData: cloneJobCreateFormData(formDataRef.current),
      savedPitchUrl: String(savedPitchUrlRef.current || "").trim(),
      pendingBlob:
        pitchPendingBlobRef.current instanceof Blob && pitchPendingBlobRef.current.size > 0
          ? pitchPendingBlobRef.current
          : null,
      pendingMime: pitchPendingMimeRef.current || "",
    };
  }, [baselineFingerprint]);

  const onFunctionalAreaChange = useCallback((event) => {
    setFormData((prev) => ({ ...prev, functionalArea: event.target.value }));
  }, []);

  const addJobApplyCountryCode = useCallback((iso) => {
    const c = String(iso || "").trim().toUpperCase();
    if (!JOB_CREATE_COUNTRY_ISO_SET.has(c)) return;
    setFormData((prev) => {
      const prevCodes = Array.isArray(prev.defaultApplyCountryCodes) ? prev.defaultApplyCountryCodes : [];
      if (prevCodes.includes(c)) return prev;
      if (prevCodes.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) return prev;
      return { ...prev, defaultApplyCountryCodes: [...prevCodes, c] };
    });
    setJobApplyCountriesInputDraft("");
  }, []);

  const removeJobApplyCountryCode = useCallback((iso) => {
    setFormData((prev) => ({
      ...prev,
      defaultApplyCountryCodes: (Array.isArray(prev.defaultApplyCountryCodes) ? prev.defaultApplyCountryCodes : []).filter(
        (x) => x !== iso
      ),
    }));
  }, []);

  const addJobApplyCountryGroup = useCallback((groupCodes) => {
    const list = [
      ...new Set(
        (groupCodes || [])
          .map((code) => String(code || "").trim().toUpperCase())
          .filter((code) => JOB_CREATE_COUNTRY_ISO_SET.has(code))
      ),
    ];
    if (!list.length) return;
    setFormData((prev) => {
      const prevCodes = Array.isArray(prev.defaultApplyCountryCodes) ? prev.defaultApplyCountryCodes : [];
      const seen = new Set(prevCodes);
      const out = [...prevCodes];
      for (const code of list) {
        if (seen.has(code)) continue;
        if (out.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) break;
        seen.add(code);
        out.push(code);
      }
      return { ...prev, defaultApplyCountryCodes: out };
    });
    setJobApplyCountriesInputDraft("");
  }, []);

  const jobPostPreviewModel = useMemo(
    () => buildJobPostPreviewSnapshot(formData, savedPitchUrl, currentUser),
    [formData, savedPitchUrl, currentUser]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const previewResume = await consumeHiringJobCreatePreviewResumeAsync(location.search || "");
        if (cancelled) return;
        if (previewResume) {
          const base = getInitialJobCreateFormFromWindowUrl(currentUser);
          const nextForm = mergeJobCreateAuthRecovery(base, { fields: previewResume.fields });
          const pitchRecovered = String(previewResume.savedPitchUrl || "").trim();
          draftRoleNavRef.current = resolveHiringDraftRoleId(location);
          let draftParam = null;
          try {
            draftParam = new URLSearchParams(location.search || "").get("draft");
          } catch {
            /* ignore */
          }
          suppressServerDraftFormHydrateOnceRef.current = Boolean(draftParam && isJobPostDraftQueryId(draftParam));
          setFormData(nextForm);
          setSavedPitchUrl(pitchRecovered);
          setBaselineFingerprint(
            String(previewResume.baselineFingerprint || "").trim() ||
              fingerprintJobCreateState(nextForm, pitchRecovered, "")
          );
          pitchPendingBlobRef.current = null;
          pitchPendingMimeRef.current = "";
          pitchPendingDurationSecRef.current = 0;
          setPitchPendingDurationSec(0);
          bumpPitchDirtySignal();
          return;
        }

        if (resolveHiringDraftRoleId(location)) return;
        try {
          const draftParam = new URLSearchParams(location.search || "").get("draft");
          if (draftParam && isJobPostDraftQueryId(draftParam)) return;
        } catch {
          /* ignore */
        }
        const loaded = loadHiringJobCreateAuthRecovery();
        if (!loaded) return;
        const pitchRecovered = String(loaded.savedPitchUrl || "").trim();
        const nextForm = mergeJobCreateAuthRecovery(formDataRef.current, loaded);
        setFormData(nextForm);
        setSavedPitchUrl(pitchRecovered);
        setBaselineFingerprint(fingerprintJobCreateState(nextForm, pitchRecovered, ""));
        setRecoveryNotice("We restored your unsaved job from before you signed in again.");
      } finally {
        if (!cancelled) setHydrationAllowed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, bumpPitchDirtySignal, currentUser, location.key, location.search]);

  useEffect(() => {
    if (!hydrationAllowed) return;
    const id = resolveHiringDraftRoleId(location);
    if (id) {
      if (draftRoleNavRef.current !== id) {
        draftRoleNavRef.current = id;
        const merged = loadHiringJobDraft(id, buildJobCreateInitialFormSnapshot(currentUser));
        const normalized = {
          ...merged,
          responsibilities: normalizeRichTextStorage(merged.responsibilities),
          requirements: normalizeRichTextStorage(merged.requirements),
        };
        setFormData(normalized);
        setJobApplyCountriesInputDraft("");
        setBaselineFingerprint(fingerprintJobCreateState(normalized, "", ""));
      }
    } else if (draftRoleNavRef.current != null) {
      draftRoleNavRef.current = null;
      const cleared = buildJobCreateInitialFormSnapshot(currentUser);
      setFormData(cleared);
      setJobApplyCountriesInputDraft("");
      setBaselineFingerprint(fingerprintJobCreateState(cleared, "", ""));
    }
  }, [
    hydrationAllowed,
    location.pathname,
    location.search,
    currentUser?.recruitmentProfile?.defaultAdvertisingCurrency,
    currentUser?.recruitmentProfile?.defaultApplyAudience,
    JSON.stringify(currentUser?.recruitmentProfile?.defaultApplyCountryCodes || []),
  ]);

  useEffect(() => {
    if (!hydrationAllowed) return;
    const draftId = String(new URLSearchParams(location.search).get("draft") || "").trim();
    if (!draftId) {
      setCurrentJobPostId("");
      return;
    }
    // Only UUID draft ids are server-backed job post drafts.
    // Local drafts (e.g. "dup-...") must not hit the UUID endpoint.
    if (!isJobPostDraftQueryId(draftId)) {
      setCurrentJobPostId("");
      return;
    }
    const token = String(authToken || "").trim();
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setSubmitError("");
        const { jobPost } = await getHiringJobPost(draftId, token);
        if (cancelled) return;
        setCurrentJobPostId(String(jobPost.id || ""));
        if (suppressServerDraftFormHydrateOnceRef.current) {
          suppressServerDraftFormHydrateOnceRef.current = false;
          clearHiringJobCreateAuthRecovery();
          setRecoveryNotice("");
          return;
        }
        const applyFromPost = readJobPostApplyFromPost(jobPost);
        const normalized = {
          title: jobPost.title || "",
          functionalArea: jobPost.functionalArea || jobPost.department || "",
          country: jobPost.country || "",
          city: jobPost.city || "",
          seniorityLevel: jobPost.seniorityLevel || "",
          workMode: jobPost.workMode || "",
          employmentType: jobPost.employmentType || "",
          salaryCurrency: coalesceJobSalaryCurrencyFromPost(jobPost.salaryCurrency, currentUser),
          salaryMin: jobPost.salaryMin == null ? "" : String(jobPost.salaryMin),
          salaryMax: jobPost.salaryMax == null ? "" : String(jobPost.salaryMax),
          summary: jobPost.summary || "",
          responsibilities: normalizeRichTextStorage(jobPost.responsibilities),
          requirements: normalizeRichTextStorage(jobPost.requirements),
          defaultApplyAudience: applyFromPost.defaultApplyAudience,
          defaultApplyCountryCodes: applyFromPost.defaultApplyCountryCodes,
        };
        const pitchLine = jobPost.pitchVideoUrl || "";
        setFormData(normalized);
        setJobApplyCountriesInputDraft("");
        setSavedPitchUrl(pitchLine);
        setBaselineFingerprint(fingerprintJobCreateState(normalized, pitchLine, ""));
        clearHiringJobCreateAuthRecovery();
        setRecoveryNotice("");
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Could not load saved draft.";
        setSubmitError(msg);
        if (isAuthRecoveryError(err)) {
          const loaded = loadHiringJobCreateAuthRecovery();
          if (loaded) {
            const recovered = mergeJobCreateAuthRecovery(buildJobCreateInitialFormSnapshot(currentUser), loaded);
            const pitchLine = String(loaded.savedPitchUrl || "").trim();
            setFormData(recovered);
            setSavedPitchUrl(pitchLine);
            setBaselineFingerprint(fingerprintJobCreateState(recovered, pitchLine, ""));
            setRecoveryNotice("We restored your unsaved job from before you signed in again.");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrationAllowed, authToken, location.search, currentUser?.recruitmentProfile?.defaultAdvertisingCurrency]);

  const buildSubmitPayload = useCallback(
    (actionType, overrides = {}) => {
      const functionalArea =
        overrides.functionalArea !== undefined
          ? String(overrides.functionalArea || "").trim()
          : formData.functionalArea.trim();
      return {
        action: actionType,
        title: formData.title.trim(),
        functionalArea,
        country: String(formData.country || "").trim().toUpperCase() || null,
        city: formData.city.trim() || null,
        seniorityLevel: String(formData.seniorityLevel || "").trim() || null,
        workMode: String(formData.workMode || "").trim() || null,
        employmentType: String(formData.employmentType || "").trim() || null,
        salaryCurrency: hasJobSalaryCurrency(formData.salaryCurrency)
          ? String(formData.salaryCurrency).trim().toUpperCase()
          : null,
        defaultApplyAudience: formData.defaultApplyAudience === "countries" ? "countries" : "anyone",
        defaultApplyCountryCodes: (() => {
          const aud = formData.defaultApplyAudience === "countries" ? "countries" : "anyone";
          const raw = Array.isArray(formData.defaultApplyCountryCodes) ? formData.defaultApplyCountryCodes : [];
          const codes = raw
            .map((x) => String(x || "").trim().toUpperCase())
            .filter((c) => /^[A-Z]{2}$/.test(c));
          return aud === "anyone" ? [] : codes;
        })(),
        salaryMin: formData.salaryMin === "" ? null : Number(formData.salaryMin),
        salaryMax: formData.salaryMax === "" ? null : Number(formData.salaryMax),
        summary: formData.summary.trim(),
        responsibilities: formData.responsibilities,
        requirements: formData.requirements,
        pitchVideoUrl:
          savedPitchUrl && !String(savedPitchUrl).startsWith("blob:") ? savedPitchUrl : null,
      };
    },
    [formData, savedPitchUrl]
  );

  const persistJobPost = useCallback(
    async (actionType, overrides = {}) => {
      const token = String(authToken || "").trim();
      if (!token) throw new Error("Sign in again to save this job post.");
      const payload = buildSubmitPayload(actionType, overrides);
      if (currentJobPostId) {
        const { jobPost } = await patchHiringJobPost(currentJobPostId, payload, token);
        return jobPost;
      }
      const { jobPost } = await postHiringJobPost(payload, token);
      return jobPost;
    },
    [authToken, buildSubmitPayload, currentJobPostId]
  );

  /** If the pitch modal has a finished recording (pending blob), mirror it into `savedPitchUrl` so the pitch box shows it before save. */
  const syncModalRecordingToSavedPitchUrl = useCallback(() => {
    const blob = pitchPendingBlobRef.current;
    if (!(blob instanceof Blob) || blob.size === 0) return;
    flushSync(() => {
      setSavedPitchUrl((prevUrl) => {
        if (prevUrl && String(prevUrl).startsWith("blob:")) URL.revokeObjectURL(prevUrl);
        return URL.createObjectURL(blob);
      });
    });
    bumpPitchDirtySignal();
  }, [bumpPitchDirtySignal]);

  /** Upload recorded blob after job post exists; returns new pitch URL or null if nothing pending. */
  const uploadPendingPitchToServer = useCallback(
    async (jobPostId) => {
      const id = String(jobPostId || "").trim();
      const blobRef = pitchPendingBlobRef.current;
      if (!id || !(blobRef instanceof Blob) || blobRef.size === 0) return null;
      const mime = pitchPendingMimeRef.current || "video/webm";
      const blob = blobRef.slice();
      const { pitchVideoUrl } = await postHiringJobPitchVideo(id, blob, mime, authToken);
      setSavedPitchUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return pitchVideoUrl;
      });
      pitchPendingBlobRef.current = null;
      pitchPendingMimeRef.current = "";
      pitchPendingDurationSecRef.current = 0;
      setPitchPendingDurationSec(0);
      bumpPitchDirtySignal();
      captureAnalyticsEvent("job_pitch_saved", {
        job_post_id: id,
      });
      return pitchVideoUrl;
    },
    [authToken, bumpPitchDirtySignal]
  );

  const updateField = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const submitter = event.nativeEvent?.submitter;
    const action = submitter?.value;
    setSubmitError("");
    setRecoveryNotice("");

    if (action === "publish") {
      const flushedFa =
        functionalAreaRef.current?.flushDraftAndGetSerialized?.() ??
        String(formData.functionalArea || "").trim();
      setFormData((prev) => ({ ...prev, functionalArea: flushedFa }));
      syncJobCreatePublishValidity(form, formData, {
        validateFunctionalArea: true,
        functionalAreaOverride: flushedFa,
      });
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      setShowPublishConfirm(true);
      return;
    }

    if (action !== "draft") return;

    syncModalRecordingToSavedPitchUrl();

    const draftFlushedFa =
      functionalAreaRef.current?.flushDraftAndGetSerialized?.() ??
      String(formData.functionalArea || "").trim();
    const mergedForDraftCheck = { ...formData, functionalArea: draftFlushedFa };

    if (!hasAnyDraftContent(mergedForDraftCheck, savedPitchUrl, pitchPendingBlobRef)) {
      if (draftFlushedFa !== String(formData.functionalArea || "").trim()) {
        setFormData((prev) => ({ ...prev, functionalArea: draftFlushedFa }));
      }
      clearJobCreatePublishCustomValidity(form);
      setSubmitError(
        "Add at least the job title or another job detail (for example job functions, country, or description) before saving a draft."
      );
      return;
    }

    setFormData((prev) => ({ ...prev, functionalArea: draftFlushedFa }));

    syncJobCreatePublishValidity(form, mergedForDraftCheck, {
      validateFunctionalArea: false,
      functionalAreaOverride: draftFlushedFa,
    });
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setSaveBusy(true);
    try {
      const jobPost = await persistJobPost("draft", { functionalArea: draftFlushedFa });
      setCurrentJobPostId(String(jobPost.id || ""));
      let pitchUploadFailed = false;
      let uploadedPitchUrl = null;
      try {
        setPitchUploadBusy(true);
        uploadedPitchUrl = await uploadPendingPitchToServer(String(jobPost.id || ""));
      } catch (upErr) {
        pitchUploadFailed = true;
        setSubmitError(upErr instanceof Error ? upErr.message : "Could not upload pitch video.");
      } finally {
        setPitchUploadBusy(false);
      }
      if (!pitchUploadFailed) {
        const pitchForFp =
          uploadedPitchUrl ||
          (!String(savedPitchUrl).startsWith("blob:") ? savedPitchUrl : "");
        const pendingKey =
          pitchPendingBlobRef.current instanceof Blob && pitchPendingBlobRef.current.size > 0
            ? `${pitchPendingBlobRef.current.size}:${pitchPendingMimeRef.current || ""}`
            : "";
        setBaselineFingerprint(
          fingerprintJobCreateState(mergedForDraftCheck, String(pitchForFp || ""), pendingKey)
        );
      }
      if (!currentJobPostId && jobPost.id) {
        navigate(`/hiring/jobs/new?draft=${encodeURIComponent(String(jobPost.id))}`, { replace: true });
      }
      const editingDraftRoleId = resolveHiringDraftRoleId(location);
      if (editingDraftRoleId) {
        saveHiringJobDraft(editingDraftRoleId, mergedForDraftCheck);
      }
      clearHiringJobCreateAuthRecovery();
      setRecoveryNotice("");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not save draft.");
      if (isAuthRecoveryError(err)) {
        saveHiringJobCreateAuthRecovery(mergedForDraftCheck, savedPitchUrl);
      }
    } finally {
      setSaveBusy(false);
    }
  };

  const confirmPublish = async () => {
    setShowPublishConfirm(false);
    setSaveBusy(true);
    setSubmitError("");
    syncModalRecordingToSavedPitchUrl();
    const flushedFa =
      functionalAreaRef.current?.flushDraftAndGetSerialized?.() ??
      String(formData.functionalArea || "").trim();
    const mergedPublish = { ...formData, functionalArea: flushedFa };
    const publishForm =
      typeof document !== "undefined" ? document.querySelector("form.job-create-form") : null;
    if (publishForm instanceof HTMLFormElement) {
      syncJobCreatePublishValidity(publishForm, mergedPublish, {
        validateFunctionalArea: true,
        functionalAreaOverride: flushedFa,
      });
      if (!publishForm.checkValidity()) {
        publishForm.reportValidity();
        setSaveBusy(false);
        return;
      }
    }
    try {
      setFormData((prev) => ({ ...prev, functionalArea: flushedFa }));
      const jobPost = await persistJobPost("publish", { functionalArea: flushedFa });
      captureAnalyticsEvent("job_published", {
        job_post_id: String(jobPost.id || ""),
      });
      setCurrentJobPostId(String(jobPost.id || ""));
      let pitchUploadFailed = false;
      let uploadedPitchUrl = null;
      try {
        setPitchUploadBusy(true);
        uploadedPitchUrl = await uploadPendingPitchToServer(String(jobPost.id || ""));
      } catch (upErr) {
        pitchUploadFailed = true;
        setSubmitError(upErr instanceof Error ? upErr.message : "Could not upload pitch video.");
      } finally {
        setPitchUploadBusy(false);
      }
      if (!pitchUploadFailed) {
        const pitchForFp =
          uploadedPitchUrl ||
          (!String(savedPitchUrl).startsWith("blob:") ? savedPitchUrl : "");
        const pendingKey =
          pitchPendingBlobRef.current instanceof Blob && pitchPendingBlobRef.current.size > 0
            ? `${pitchPendingBlobRef.current.size}:${pitchPendingMimeRef.current || ""}`
            : "";
        setBaselineFingerprint(
          fingerprintJobCreateState(mergedPublish, String(pitchForFp || ""), pendingKey)
        );
      }
      clearHiringJobCreateAuthRecovery();
      setRecoveryNotice("");
      navigate("/hiring-dashboard");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not publish job post.");
      if (isAuthRecoveryError(err)) {
        saveHiringJobCreateAuthRecovery(mergedPublish, savedPitchUrl);
      }
    } finally {
      setSaveBusy(false);
    }
  };

  const onCloseRequest = () => {
    if (pitchUploadBusy) {
      setShowCloseConfirm(true);
      return;
    }
    if (isJobCreateDirty) {
      setShowCloseConfirm(true);
      return;
    }
    navigate("/hiring-dashboard");
  };

  const confirmCloseToDashboard = () => {
    setShowCloseConfirm(false);
    navigate("/hiring-dashboard");
  };

  const closePitchModal = useCallback(() => {
    setShowPitchModal(false);
  }, []);

  const handlePitchModalRecordingChange = useCallback(
    (blob, mime, durationSec = 0) => {
      pitchPendingBlobRef.current = blob;
      pitchPendingMimeRef.current = mime || "";
      const nextDuration =
        Number.isFinite(Number(durationSec)) && Number(durationSec) > 0 ? Number(durationSec) : 0;
      pitchPendingDurationSecRef.current = nextDuration;
      setPitchPendingDurationSec(nextDuration);
      bumpPitchDirtySignal();
    },
    [bumpPitchDirtySignal]
  );

  const handleJobCreatePitchModalSubmit = useCallback(
    async ({ blob, mime }) => {
      if (currentJobPostId && blob instanceof Blob && blob.size > 0) {
        setPitchUploadBusy(true);
        try {
          const { pitchVideoUrl } = await postHiringJobPitchVideo(currentJobPostId, blob, mime, authToken);
          setSavedPitchUrl((prev) => {
            if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
            return pitchVideoUrl;
          });
          pitchPendingBlobRef.current = null;
          pitchPendingMimeRef.current = "";
          pitchPendingDurationSecRef.current = 0;
          setPitchPendingDurationSec(0);
          bumpPitchDirtySignal();
          captureAnalyticsEvent("job_pitch_saved", {
            job_post_id: String(currentJobPostId),
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Could not upload pitch video.";
          setSubmitError(msg);
          throw err instanceof Error ? err : new Error(msg);
        } finally {
          setPitchUploadBusy(false);
        }
      } else {
        const nextUrl = URL.createObjectURL(blob);
        setSavedPitchUrl((prevUrl) => {
          if (prevUrl && prevUrl !== nextUrl && String(prevUrl).startsWith("blob:")) {
            URL.revokeObjectURL(prevUrl);
          }
          return nextUrl;
        });
        // Keep bytes for upload after "Save draft" / "Publish" creates a job post id (uploadPendingPitchToServer).
        pitchPendingBlobRef.current = blob;
        pitchPendingMimeRef.current = mime || "";
        bumpPitchDirtySignal();
      }
    },
    [authToken, bumpPitchDirtySignal, currentJobPostId]
  );

  const resetJobCreateToSavedBaseline = useCallback(() => {
    const snap = baselineJobCreateSnapshotRef.current;
    if (!snap) return;
    if (showPitchModal) closePitchModal();
    functionalAreaRef.current?.clearComposeDraft?.();

    const prevUrl = String(savedPitchUrlRef.current || "");
    const nextUrl = String(snap.savedPitchUrl || "");
    if (prevUrl.startsWith("blob:") && prevUrl !== nextUrl) {
      URL.revokeObjectURL(prevUrl);
    }

    setFormData(cloneJobCreateFormData(snap.formData));
    setJobApplyCountriesInputDraft("");
    setSavedPitchUrl(nextUrl);
    pitchPendingBlobRef.current = snap.pendingBlob;
    pitchPendingMimeRef.current = snap.pendingMime || "";
    bumpPitchDirtySignal();
    setSubmitError("");
    setRecoveryNotice("");

    const roleDraftId = resolveHiringDraftRoleId(location);
    if (roleDraftId) {
      saveHiringJobDraft(roleDraftId, cloneJobCreateFormData(snap.formData));
    }
  }, [bumpPitchDirtySignal, closePitchModal, location, showPitchModal]);

  useEffect(
    () => () => {
      if (savedPitchUrl && String(savedPitchUrl).startsWith("blob:")) {
        URL.revokeObjectURL(savedPitchUrl);
      }
    },
    [savedPitchUrl]
  );

  const handleDeletePitch = async () => {
    if (saveBusy || pitchUploadBusy) return;
    const current = String(savedPitchUrl || "");
    if (!current) return;

    // Local-only preview (blob:) — clear it and pending bytes.
    if (current.startsWith("blob:")) {
      setSavedPitchUrl((prev) => {
        if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
        return "";
      });
      pitchPendingBlobRef.current = null;
      pitchPendingMimeRef.current = "";
      pitchPendingDurationSecRef.current = 0;
      setPitchPendingDurationSec(0);
      bumpPitchDirtySignal();
      return;
    }

    // Uploaded pitch — delete from server if we have an id.
    if (!currentJobPostId) {
      setSavedPitchUrl("");
      bumpPitchDirtySignal();
      return;
    }

    setSubmitError("");
    setPitchUploadBusy(true);
    try {
      await deleteHiringJobPitchVideo(currentJobPostId, authToken);
      setSavedPitchUrl("");
      pitchPendingBlobRef.current = null;
      pitchPendingMimeRef.current = "";
      pitchPendingDurationSecRef.current = 0;
      setPitchPendingDurationSec(0);
      bumpPitchDirtySignal();

      // If the only dirty bit was the pitch, treat the delete as "saved" since it hit the API.
      if (!isJobCreateDirtyIgnoringPitch) {
        setBaselineFingerprint(fingerprintJobCreateState(formDataRef.current, "", ""));
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not delete pitch video.");
    } finally {
      setPitchUploadBusy(false);
    }
  };

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <div className="job-create-shell">
        <main className="nd1-shell nd1-hiring-job-create-main jw-recruitment-main job-create-page">
        <section className="nd1-hiring-job-create-section job-create-page-section">
          <div className="job-create-hero nd1-hiring-job-create-hero">
            <Link className="back-link nd1-recruitment-profile-back" to="/hiring-dashboard">
              &larr; Back to recruitment dashboard
            </Link>
            <p className="eyebrow nd1-label">Create a job post</p>
            <h1>Create a new job post</h1>
            <p className="subtitle">
              Be clear and honest — skip jargon unless your audience knows it. Keep it simple and direct.
            </p>
            {recoveryNotice ? (
              <p className="job-create-recovery-notice" role="status">
                {recoveryNotice}
              </p>
            ) : null}
          </div>

          <form className="job-create-form nd1-hiring-job-create-form" onSubmit={onSubmit} noValidate>
            <section className="nd1-hiring-job-create-form-section" aria-labelledby="job-create-details-heading">
              <h2 className="nd1-hiring-job-create-form-section-title" id="job-create-details-heading">
                Job details
              </h2>
            <label>
              <span className="job-search-label">Job title</span>
              <input
                id="job-create-title"
                type="text"
                value={formData.title}
                onChange={updateField("title")}
                required
              />
            </label>
            <label htmlFor="job-create-functional-area">
              <span className="job-search-label">Job functions</span>
              <p className="saved-search-meta job-create-helper-text">
                add a comma-separated list. You can add up to five.
              </p>
              <FunctionalAreaPillsInput
                ref={functionalAreaRef}
                composeId="job-create-functional-area"
                value={formData.functionalArea}
                onChange={onFunctionalAreaChange}
                placeholder="e.g. Technology, Marketing, Operations"
              />
            </label>
            <input
              id="job-create-functional-area-validity"
              type="text"
              className="profile-manage-sr-only"
              tabIndex={-1}
              readOnly
              value=""
              aria-hidden="true"
            />
            <div className="job-create-grid-2">
              <label>
                <span className="job-search-label">Job primary country</span>
                <select id="job-create-country" value={formData.country} onChange={updateField("country")}>
                  <option value="">Please select</option>
                  {COUNTRIES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="job-search-label">Job Primary town/city (optional)</span>
                <input
                  id="job-create-city"
                  type="text"
                  value={formData.city}
                  onChange={updateField("city")}
                  placeholder="e.g. Edinburgh"
                  maxLength={200}
                />
              </label>
            </div>
            <label>
              <span className="job-search-label">Seniority level</span>
              <select
                id="job-create-seniority-level"
                value={formData.seniorityLevel}
                onChange={updateField("seniorityLevel")}
              >
                <option value="">Please select</option>
                {SENIORITY_LEVEL_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {formatSeniorityDisplay(v)}
                  </option>
                ))}
              </select>
            </label>
            <div className="job-create-grid-2">
              <label>
                <span className="job-search-label">Work mode</span>
                <select id="job-create-work-mode" value={formData.workMode} onChange={updateField("workMode")}>
                  <option value="">Please select</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">On-site</option>
                  <option value="mixed">Mix of remote, hybrid and onsite</option>
                </select>
              </label>
              <label>
                <span className="job-search-label">Employment type</span>
                <select
                  id="job-create-employment-type"
                  value={formData.employmentType}
                  onChange={updateField("employmentType")}
                >
                  <option value="">Please select</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract (e.g. Personal, B2B)</option>
                  <option value="internship">Internship</option>
                </select>
              </label>
            </div>
            </section>
            <section className="nd1-hiring-job-create-form-section" aria-labelledby="job-create-pay-heading">
              <h2 className="nd1-hiring-job-create-form-section-title" id="job-create-pay-heading">
                Pay
              </h2>
            <label className="job-create-salary-currency">
              <span className="job-search-label">Salary currency</span>
              <select
                id="job-create-salary-currency"
                value={formData.salaryCurrency}
                onChange={updateField("salaryCurrency")}
                required
              >
                <option value="">Please select</option>
                {SALARY_CURRENCY_OPTIONS.map(({ value, symbol, name }) => (
                  <option key={value} value={value}>
                    {symbol} {value} — {name}
                  </option>
                ))}
              </select>
            </label>
            <div className="job-create-grid-2 job-create-salary-amounts">
              <label>
                <span className="job-search-label">Salary minimum</span>
                <div className="job-create-salary-input-wrap">
                  {hasJobSalaryCurrency(formData.salaryCurrency) ? (
                    <span className="job-create-salary-prefix" aria-hidden="true">
                      <SalaryCurrencyIcon currency={formData.salaryCurrency} size={18} />
                      <span className="job-create-salary-acronym">
                        {String(formData.salaryCurrency).trim().toUpperCase()}
                      </span>
                    </span>
                  ) : null}
                  <input
                    id="job-create-salary-min"
                    type="number"
                    min="0"
                    value={formData.salaryMin}
                    onChange={updateField("salaryMin")}
                    required
                  />
                </div>
              </label>
              <label>
                <span className="job-search-label">Salary maximum</span>
                <div className="job-create-salary-input-wrap">
                  {hasJobSalaryCurrency(formData.salaryCurrency) ? (
                    <span className="job-create-salary-prefix" aria-hidden="true">
                      <SalaryCurrencyIcon currency={formData.salaryCurrency} size={18} />
                      <span className="job-create-salary-acronym">
                        {String(formData.salaryCurrency).trim().toUpperCase()}
                      </span>
                    </span>
                  ) : null}
                  <input
                    id="job-create-salary-max"
                    type="number"
                    min="0"
                    value={formData.salaryMax}
                    onChange={updateField("salaryMax")}
                    required
                  />
                </div>
              </label>
            </div>
            </section>
            <section className="nd1-hiring-job-create-form-section" aria-labelledby="job-create-role-heading">
              <h2 className="nd1-hiring-job-create-form-section-title" id="job-create-role-heading">
                About the role
              </h2>
            <label>
              <span className="job-search-label">Role summary</span>
              <p className="saved-search-meta job-create-helper-text">
                A brief overview that quickly captures attention so applicants understand the core of the role.
              </p>
              <textarea
                id="job-create-summary"
                value={formData.summary}
                onChange={updateField("summary")}
                rows={3}
                maxLength={600}
                required
              />
              <span className="saved-search-meta job-create-counter">
                {600 - formData.summary.length} characters remaining
              </span>
            </label>
            <JobCreateRichTextField
              label="Must-have Skills and Experience"
              helperText="Add what an applicant must have to get this job and is non-negotiable."
              value={formData.requirements}
              onChange={updateField("requirements")}
              placeholder="List the non-negotiable skills and experience."
              maxChars={1500}
            />
            <input
              id="job-create-requirements-validity"
              type="text"
              className="profile-manage-sr-only"
              tabIndex={-1}
              readOnly
              value=""
              aria-hidden="true"
            />
            <JobCreateRichTextField
              label="Job Description"
              helperText="Give a detailed description. Some points to include are the company, benefits, the purpose of the role, and why someone should apply."
              value={formData.responsibilities}
              onChange={updateField("responsibilities")}
              placeholder="Provide a detailed role description for applicants."
              maxChars={3500}
            />
            <input
              id="job-create-responsibilities-validity"
              type="text"
              className="profile-manage-sr-only"
              tabIndex={-1}
              readOnly
              value=""
              aria-hidden="true"
            />
            </section>
            <section
              className="nd1-hiring-job-create-form-section nd1-hiring-job-create-form-section--pitch job-create-pitch-section"
              aria-labelledby="job-pitch-heading"
            >
              <h2 className="nd1-hiring-job-create-form-section-title nd1-hiring-job-create-pitch-heading" id="job-pitch-heading">
                <Video className="nd1-hiring-job-create-pitch-heading-icon" strokeWidth={1.75} aria-hidden="true" />
                Your job pitch
              </h2>
              <p className="subtitle">
                Bring this role to life. Share who you are looking for, what makes this opportunity special, and what
                skills you are after. A great pitch helps the right candidates feel confident and excited to apply. No
                need for a formal polished pitch — be honest, clear, and inviting.
              </p>
              <div className="job-create-pitch-webcam" aria-label="Webcam">
                <button
                  type="button"
                  className="large-filled-button job-create-webcam-btn"
                  onClick={() => setShowPitchModal(true)}
                >
                  <svg
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M12 17.4A7.2 7.2 0 1 0 12 3a7.2 7.2 0 0 0 0 14.4m0 0V21m-4.5 0h9m-1.8-10.8a2.7 2.7 0 1 1-5.4 0 2.7 2.7 0 0 1 5.4 0" />
                  </svg>
                  <span>{savedPitchUrl ? "Replace video pitch" : "Create a video pitch"}</span>
                </button>
              </div>
              {savedPitchUrl ? (
                <p className="subtitle job-create-pitch-saved-helper">
                  You&apos;ve already got a pitch saved — watch it below before recording a new one.
                </p>
              ) : null}
              {savedPitchUrl ? (
                <div className="job-create-saved-pitch">
                  <PitchVideoPlayer
                    className="job-create-saved-pitch-video"
                    src={savedPitchUrl}
                    ariaLabel="Your job pitch video"
                    knownDurationSec={
                      String(savedPitchUrl).startsWith("blob:") ? pitchPendingDurationSec : 0
                    }
                  />
                  <div className="job-create-pitch-delete-row">
                    <button
                      type="button"
                      className="job-create-pitch-delete-btn"
                      onClick={handleDeletePitch}
                      disabled={saveBusy || pitchUploadBusy}
                      aria-label="Delete pitch video"
                      title="Delete pitch video"
                    >
                      <svg
                        className="icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="m18 7-.886 10.342c-.111 1.29-.166 1.936-.453 2.424a2.5 2.5 0 0 1-1.078.99c-.511.244-1.16.244-2.455.244h-2.256c-1.296 0-1.944 0-2.455-.244a2.5 2.5 0 0 1-1.078-.99c-.287-.488-.342-1.134-.453-2.424L6 7m-1.5-.5h4.615m0 0 .386-2.672c.112-.486.516-.828.98-.828h3.038c.464 0 .867.342.98.828l.386 2.672m-5.77 0h5.77m0 0H19.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete pitch
                    </button>
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                className="job-create-pitch-info-link"
                onClick={() => setShowMobilePitchInfo(true)}
              >
                <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 16v-5h-.5m0 5h1M12 8.5V8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Use your mobile for video pitch?
              </button>
            </section>
            <section
              className={`nd1-hiring-job-create-form-section nd1-hiring-job-create-form-section--settings job-create-application-settings-section${
                applicationSettingsExpanded
                  ? " nd1-hiring-job-create-form-section--settings-expanded"
                  : " nd1-hiring-job-create-form-section--settings-collapsed"
              }`}
              aria-labelledby="job-application-settings-heading"
            >
              <button
                type="button"
                className="nd1-hiring-job-create-settings-toggle"
                id="job-application-settings-heading"
                aria-expanded={applicationSettingsExpanded}
                aria-controls="job-application-settings-panel"
                onClick={() => setApplicationSettingsExpanded((open) => !open)}
              >
                <span className="nd1-hiring-job-create-settings-toggle-title">
                  <Briefcase className="nd1-hiring-job-create-form-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Application settings
                </span>
                <span className="nd1-hiring-job-create-settings-toggle-meta">
                  <span className="nd1-hiring-job-create-settings-toggle-hint">
                    {applicationSettingsExpanded ? "Hide" : "Using your recruitment defaults"}
                  </span>
                  <ChevronDown
                    className={`nd1-hiring-job-create-settings-toggle-chevron${applicationSettingsExpanded ? " is-open" : ""}`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </span>
              </button>
              {applicationSettingsExpanded ? (
              <div id="job-application-settings-panel" className="nd1-hiring-job-create-settings-panel">
              <p className="subtitle nd1-hiring-job-create-form-section-lead">
                Choose who can apply to this role. We&apos;ve pre-filled your recruitment defaults — change them here if you need to.
              </p>
              <fieldset
                className="profile-manage-field-group recruitment-default-apply-fieldset nd1-hiring-job-create-settings-fieldset"
                aria-labelledby="job-create-default-apply-legend"
              >
                <p className="nd1-hiring-job-create-settings-subheading" id="job-create-default-apply-legend">
                  <Users className="nd1-hiring-job-create-form-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Who can apply for this job?
                </p>
                <div
                  className="recruitment-default-apply-radios"
                  role="radiogroup"
                  aria-labelledby="job-create-default-apply-legend"
                >
                  <label className="recruitment-default-apply-radio">
                    <input
                      type="radio"
                      className="checkbox-control checkbox-control--primary-2"
                      name="job-create-default-apply-audience"
                      checked={formData.defaultApplyAudience === "anyone"}
                      onChange={() => {
                        setFormData((prev) => ({ ...prev, defaultApplyAudience: "anyone", defaultApplyCountryCodes: [] }));
                        setJobApplyCountriesInputDraft("");
                      }}
                    />
                    <span>Anyone on JOBUWIN</span>
                  </label>
                  <label className="recruitment-default-apply-radio">
                    <input
                      type="radio"
                      className="checkbox-control checkbox-control--primary-2"
                      name="job-create-default-apply-audience"
                      checked={formData.defaultApplyAudience === "countries"}
                      onChange={() => setFormData((prev) => ({ ...prev, defaultApplyAudience: "countries" }))}
                    />
                    <span>People from specific countries</span>
                  </label>
                </div>
                {formData.defaultApplyAudience === "countries" ? (
                  <div className="profile-manage-country-select-group profile-manage-country-field recruitment-default-apply-countries">
                    <label
                      className="profile-manage-country-pills-field-label"
                      htmlFor="job-create-default-apply-countries-input"
                    >
                      Countries ({(formData.defaultApplyCountryCodes || []).length}/{MAX_DEFAULT_APPLY_COUNTRY_CODES})
                    </label>
                    <div className="recruitment-default-apply-picker-stack">
                      <WorkCountriesPillPicker
                        disabled={false}
                        countryCodes={formData.defaultApplyCountryCodes || []}
                        inputDraft={jobApplyCountriesInputDraft}
                        onDraftChange={setJobApplyCountriesInputDraft}
                        onAddCode={addJobApplyCountryCode}
                        onRemoveCode={removeJobApplyCountryCode}
                        maxCodes={MAX_DEFAULT_APPLY_COUNTRY_CODES}
                        inputId="job-create-default-apply-countries-input"
                        placeholderDisabled="Search countries"
                      />
                      {JOB_CREATE_EEA_GROUP ? (
                        <div
                          className="profile-manage-country-group-shortcuts recruitment-default-apply-eea-shortcut"
                          role="group"
                          aria-label="Add all countries from a predefined group"
                        >
                          <button
                            type="button"
                            className="profile-manage-country-group-shortcut-link"
                            title={`Add: ${JOB_CREATE_EEA_GROUP.countryCodes.join(", ")}`}
                            onClick={() => addJobApplyCountryGroup(JOB_CREATE_EEA_GROUP.countryCodes)}
                          >
                            <span className="profile-manage-country-group-shortcut-link__plus" aria-hidden="true">
                              +
                            </span>
                            <span className="profile-manage-country-group-shortcut-link__label">
                              {JOB_CREATE_EEA_GROUP.label}
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </fieldset>
              <input
                id="job-create-default-apply-validity"
                type="text"
                className="profile-manage-sr-only"
                tabIndex={-1}
                readOnly
                value=""
                aria-hidden="true"
              />
              </div>
              ) : null}
            </section>
            <div
              className={`jw-rec-job-toolbar job-create-actions nd1-recruitment-profile-footer nd1-hiring-job-create-footer${isJobCreateDirty ? " job-actions-float--unsaved job-create-actions--unsaved" : ""}`}
              role="toolbar"
              aria-label={
                isJobCreateDirty ? "Job post actions, unsaved changes" : "Job post actions"
              }
            >
              {pitchUploadBusy ? (
                <p className="profile-manage-unsaved-hint job-create-uploading-hint" role="status" aria-live="polite">
                  <span className="job-create-spinner" aria-hidden="true" />
                  Uploading pitch video…
                </p>
              ) : null}
              {isJobCreateDirty ? (
                <p className="profile-manage-unsaved-hint" role="status">
                  Changes are not saved.
                </p>
              ) : null}
              {submitError ? (
                <p className="job-create-actions-error apply-shine-error" role="alert">
                  {submitError}
                </p>
              ) : null}
              <div className="job-create-actions-row">
                <button
                  type="button"
                  className="job-create-preview-link"
                  onClick={() => setJobPreviewOpen(true)}
                >
                  <svg className="icon job-create-preview-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 4h18m-1.9 0v9.778c0 .471-.19.923-.527 1.257-.338.333-.796.52-1.273.52H6.7c-.477 0-.935-.187-1.273-.52a1.77 1.77 0 0 1-.527-1.257V4m2.6 16 4.5-4.444L16.5 20"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  See Preview
                </button>
                <button
                  type="submit"
                  formNoValidate
                  className="small-outline-button job-create-action-draft"
                  name="submitAction"
                  value="draft"
                  disabled={saveBusy || pitchUploadBusy || !isJobCreateDirty}
                >
                  {pitchUploadBusy ? "Uploading…" : "Save as a Draft"}
                </button>
                <button
                  type="submit"
                  className="large-filled-button job-create-action-publish"
                  name="submitAction"
                  value="publish"
                  disabled={saveBusy || pitchUploadBusy}
                >
                  {pitchUploadBusy ? "Uploading…" : "Save & Publish"}
                </button>
                <button
                  type="button"
                  className="hiring-job-create-reset-btn recruitment-job-create-reset-btn"
                  onClick={resetJobCreateToSavedBaseline}
                  disabled={saveBusy || pitchUploadBusy || !isJobCreateDirty}
                  title="Discard unsaved edits and restore the last saved state"
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="small-outline-button job-create-action-cancel"
                  onClick={onCloseRequest}
                  disabled={saveBusy || pitchUploadBusy}
                >
                  {pitchUploadBusy ? "Uploading…" : "Close"}
                </button>
              </div>
            </div>
          </form>

        </section>
      </main>
      <JobPostPreviewModal open={jobPreviewOpen} onClose={() => setJobPreviewOpen(false)} job={jobPostPreviewModel} />
      <PitchRecordModal
        open={showPitchModal}
        onRequestClose={closePitchModal}
        titleId="job-create-pitch-modal-title"
        title="Lets make this job shine!"
        limitLine="Video must be 5 minutes or less."
        submitLabel="Save Job Pitch"
        submittingLabel="Uploading…"
        submitBusy={pitchUploadBusy}
        submitButtonClassName="apply-shine-submit-btn job-create-pitch-save-btn"
        onSubmitPitch={handleJobCreatePitchModalSubmit}
        analyticsContext="hiring_job_create"
        pitchVideoAriaLabel="Job pitch preview"
        onRecordingBlobChange={handlePitchModalRecordingChange}
        retainParentRecordingBlobAfterSubmit
        warnDiscardUnsavedPreview={Boolean(savedPitchUrl)}
        discardUnsavedPreviewMessage="You haven't saved this recording yet — your current pitch is still the one below."
      />
      {showPublishConfirm ? (
        <div className="apply-help-overlay" role="presentation" onClick={() => setShowPublishConfirm(false)}>
          <div
            className="apply-help-modal job-create-publish-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-confirm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="publish-confirm-title">Ready to publish the job?</h2>
            <p className="subtitle">
              Just want to make sure — once you publish the job, it is final and applicants will start seeing the role.
            </p>
            <div className="job-create-cancel-actions job-create-publish-confirm-actions">
              <button type="button" className="job-create-publish-back-btn" onClick={() => setShowPublishConfirm(false)}>
                No, Check again
              </button>
              <button
                type="button"
                className="large-filled-button"
                onClick={confirmPublish}
                disabled={saveBusy || pitchUploadBusy}
              >
                {pitchUploadBusy ? "Uploading…" : "Yes, Publish"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showCloseConfirm ? (
        <div className="apply-help-overlay" role="presentation" onClick={() => setShowCloseConfirm(false)}>
          <div
            className="apply-help-modal job-create-cancel-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="close-confirm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h4 id="close-confirm-title">Leave without saving?</h4>
            <p className="subtitle">
              {pitchUploadBusy
                ? "Your pitch video is still uploading. Leaving now may cancel the upload."
                : "You have unsaved changes. If you close now, you will lose anything that has not been saved as a draft or published."}
            </p>
            <div className="job-create-cancel-actions job-create-publish-confirm-actions">
              <button type="button" className="job-create-publish-back-btn" onClick={() => setShowCloseConfirm(false)}>
                Keep editing
              </button>
              <button type="button" className="large-filled-button job-create-cancel-confirm-btn" onClick={confirmCloseToDashboard}>
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showMobilePitchInfo ? (
        <div className="apply-help-overlay" role="presentation" onClick={() => setShowMobilePitchInfo(false)}>
          <div
            className="apply-help-modal job-create-publish-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-pitch-info-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h4 id="mobile-pitch-info-title">Using my mobile to create the video pitch</h4>
            <p className="subtitle">
              You can log in to JOBUWIN on your mobile to create a job post or save it as a draft. You can edit all
              drafts on mobile, including adding a video pitch using your mobile camera.
            </p>
            <div className="job-create-cancel-actions job-create-publish-confirm-actions job-create-info-actions">
              <button type="button" className="job-create-publish-back-btn" onClick={() => setShowMobilePitchInfo(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </RecruitmentShell>
  );
}

export default CreateJobOpeningPage;
